import { useQuery } from "@tanstack/react-query";
import { fetchSubscriptions } from "@/lib/api/handlers/SubscriptionHandling";
import { canUseWhiteLabeling } from "@/lib/utils/plan-features";
import { useEventEntitlements } from "@/lib/query/hooks/useEventEntitlements";
import type { Subscription } from "@/types/Subscription";

/**
 * Does this COMPANY get to keep its own sender display name?
 *
 * Below Advanced the send pipeline throws the tenant's alias away and stamps
 * "Veltric" on every outgoing mail (main-consumer/src/lib/sender-resolver.ts:42
 * `FORCED_DISPLAY_NAME`). Any surface that previews a `From:` has to apply the
 * same gate or it promises a display name the recipient never sees.
 *
 * WHICH company: with an `eventId` the one that OWNS the event — the mail goes
 * out under that company's plan, and the login company's subscriptions govern
 * nothing on a foreign event (and are refused for a same-company
 * EDITOR/VIEWER). `null` is the template surface, which has no event and keeps
 * the login-company read. The argument is required on purpose: a forgotten
 * caller fails typecheck instead of silently asking the wrong tenant.
 *
 * ⚠️ Scope-blind on both legs, like the consumer's gate (it reads every
 * subscription the company holds; `features.whiteLabel` is computed the same
 * way server-side), so narrowing to the event would invent a divergence the
 * send path does not have.
 *
 * While the query is in flight this reports `false`, matching the existing
 * white-label gates in the app. The consequence is bounded: the ADDRESS — the
 * part the reported bug got wrong — is already correct; only the display name
 * may render as "Veltric" for the first frames of an Advanced tenant.
 * `isResolved` is exposed for callers that would rather hold their claim back
 * than show one that flips.
 */
export function useWhiteLabelAccess(eventId: string | null): {
  hasWhiteLabel: boolean;
  isResolved: boolean;
} {
  // Both hooks every render (rules-of-hooks); the argument decides which fires.
  const entitlementsQ = useEventEntitlements(eventId);
  const companyQ = useQuery({
    queryKey: ["subscriptions", "whiteLabel"],
    queryFn: () => fetchSubscriptions("type"),
    staleTime: 5 * 60 * 1000,
    enabled: eventId === null,
  });

  if (eventId !== null) {
    const features = entitlementsQ.data?.features;
    return {
      hasWhiteLabel: features ? features.whiteLabel === true : false,
      isResolved: !!features,
    };
  }

  const subscriptions = Array.isArray(companyQ.data) ? (companyQ.data as Subscription[]) : null;

  return {
    hasWhiteLabel: subscriptions ? canUseWhiteLabeling(subscriptions) : false,
    isResolved: subscriptions !== null,
  };
}
